import type { AttributeOperator, RuleValueKind } from './attribute-rules';
import type { RuleDraftManager } from './use-rule-draft';
import { ATTRIBUTE_RULE_OPERATORS } from 'definitions';
import { cn } from 'helpers/cn';
import { ConfirmDialog } from '@/components/Dialogs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { valueKindsFor } from './attribute-rules';
import { COPY, OPERATOR_LABELS, VALUE_KIND_LABELS } from './copy';

type AttributeRuleDialogProps = {
  manager: RuleDraftManager;
  /** Where the attributes come from, when not the SDK call of a Server-side experiment. */
  help?: readonly string[];
};

const FIELD_CLASS = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

/**
 * Adds one attribute rule. The ActivationRuleDialog shape: a ConfirmDialog whose Add stays
 * disabled, with the reason beside it, until the rule would pass the API's own checks.
 */
export const AttributeRuleDialog = ({ manager, help = COPY.rules.dialogHelp }: AttributeRuleDialogProps) => {
  const { ruleDraft, parsedRule, updateRuleDraft, addRule, closeRuleDialog } = manager;
  const draft = ruleDraft ?? { attribute: '', operator: 'equals' as const, valueKind: 'text' as const, valueText: '' };
  const kinds = valueKindsFor(draft.operator);
  const isList = draft.operator === 'in' || draft.operator === 'not in';
  const invalidReason = parsedRule && !parsedRule.ok ? parsedRule.reason : undefined;

  return (
    <ConfirmDialog
      open={!!ruleDraft}
      title={COPY.rules.dialogTitle}
      onConfirm={addRule}
      onCancel={closeRuleDialog}
      disabled={!parsedRule?.ok}
      disabledReason={invalidReason}
      confirmLabel="Add"
    >
      <div className="flex flex-col gap-4">
        <div className="supporting flex flex-col gap-2">
          {help.map((line) => (
            <p key={line} className="m-0">
              {line}
            </p>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="attribute-rule-attribute" className="micro-label">
              {COPY.rules.attribute}
            </Label>
            <Input
              id="attribute-rule-attribute"
              value={draft.attribute}
              onChange={(ev) => updateRuleDraft({ attribute: ev.target.value })}
              placeholder={COPY.rules.attributePlaceholder}
              className={cn(FIELD_CLASS, 'font-mono')}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="micro-label">{COPY.rules.operator}</span>
            <Select
              value={draft.operator}
              onValueChange={(value) => updateRuleDraft({ operator: value as AttributeOperator })}
            >
              <SelectTrigger className={FIELD_CLASS} aria-label={COPY.rules.operator}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ATTRIBUTE_RULE_OPERATORS.map((operator) => (
                  <SelectItem key={operator} value={operator}>
                    {OPERATOR_LABELS[operator]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {kinds.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            {kinds.length > 1 && (
              <div className="flex flex-col gap-2">
                <span className="micro-label">{COPY.rules.valueKind}</span>
                <Select
                  value={draft.valueKind}
                  onValueChange={(value) => updateRuleDraft({ valueKind: value as RuleValueKind })}
                >
                  <SelectTrigger className={FIELD_CLASS} aria-label={COPY.rules.valueKind}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {kinds.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {VALUE_KIND_LABELS[kind]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className={cn('flex flex-col gap-2', kinds.length === 1 && 'sm:col-span-2')}>
              <Label htmlFor="attribute-rule-value" className="micro-label">
                {isList ? COPY.rules.listValue : COPY.rules.value}
              </Label>
              <Input
                id="attribute-rule-value"
                value={draft.valueText}
                onChange={(ev) => updateRuleDraft({ valueText: ev.target.value })}
                inputMode={draft.valueKind === 'number' ? 'decimal' : undefined}
                className={cn(FIELD_CLASS, 'font-mono')}
              />
            </div>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
};
